import {
    call,
    put,
    select,
} from 'redux-saga/effects';
import { AppState } from '../interface';
import {
    isCableCustomer,
    selectAppState,
    selectIsLoggedInCableCustomer,
} from '../selector';
import {
    getUrl,
    isCustomerIn3pFlowByUrl,
    updatePageUrl,
} from '../../../util/url';
import {
    Actions,
    setError,
    setFriendsAndFamily,
    setInitialTariffs,
    setLoginStatus,
    startLoading,
    stopLoading,
} from '../action';
import { getTariffs } from '../../../util/jarvis';
import {
    JarvisProduct,
    StepperData,
} from '../../../interface';
import { ErrorType } from '../enum';
import {
    deleteCookie,
    getCookie,
    getQueryParam,
    setCookie,
} from '@vfde-sails/utils';
import {
    Cookies,
    Domains,
    Endpoints,
    QueryNames,
} from '../../../enum';
import { STEPPER_DATA } from '../../../constants';
import { isNotEmptyString } from '../../../util/typeGuard';
import { getCMSConfig } from '../../../util/cms';
import {
    BIDResponse,
    LoginResponse,
    getBID,
    getCustomerType,
    getGimliLogin,
    setBID,
    validateFriendsAndFamilyToken,
} from '@vfde-sails/avc-core';
import {
    trackError,
    trackPageview,
} from '../../../tracking/tracking';
import { mountLinearStepper } from '../../../components/linearStepper';
import {
    isCookiePresent,
    set3PCookie,
} from '../../../util/cookie';
import { setElementVisibility } from '../../../util/visibility';
import { AppId } from '../../../enums/elementId';

/**
 * Updates page url with gigakombi and tariff selection
 */
export function* setPageUrl () {
    const state: AppState = yield select(selectAppState);
    yield call(updatePageUrl, state.isGigakombi, state.selectedTariffId);
}

/**
 * Tracks page view
 */
export function* trackPageViewSaga () {
    const state: AppState = yield select(selectAppState);
    yield call(trackPageview, state);
}

/**
 * Fetches and sets initial tariff details from Jarvis
 */
export function* getInitialTariffs (action: Actions) {
    try {
        yield put(startLoading(action.type));

        const tariffs: JarvisProduct[] = yield call(getTariffs);

        yield put(setInitialTariffs(tariffs));

        const isLoggedInCableCustomer: boolean = yield select(selectIsLoggedInCableCustomer);

        if (!isLoggedInCableCustomer) {
            yield call(trackPageViewSaga);
        }
    }
    catch (error) {
        yield put(setError({ error: error as Error, type: ErrorType.JARVIS }));
        yield call(trackError, error as Error, ErrorType.JARVIS);
    }
    finally {
        yield put(stopLoading(action.type));
    }
}

/**
 * Validates F&F token and redirects if invalid
 */
export function* validateFriendsAndFamily (action: Actions) {
    const queryToken = getQueryParam(QueryNames.FF);
    const cookieToken = getCookie(Cookies.FF);
    const token = queryToken ?? cookieToken;

    if (!token || !isNotEmptyString(token)) {
        return;
    }

    try {
        yield put(startLoading(action.type));
        yield call(validateFriendsAndFamilyToken, token);

        yield call(setCookie, Cookies.FF, { value: token });
        yield put(setFriendsAndFamily(true));
    }
    catch (error) {
        location.assign(getCMSConfig().errorUrls.friendsAndFamily);
    }
    finally {
        yield put(stopLoading(action.type));
    }
}

/**
 * Gets b_id information through ZP-API, sets it if exists in URL parameter
 * Returns current b_id
 */
export function* handleBannerId (action: Actions) {
    const bId = getQueryParam(QueryNames.B_ID);

    try {
        yield put(startLoading(action.type));

        if (bId && isNotEmptyString(bId)) {
            yield call(deleteCookie, Cookies.OSHOP);
            const response: BIDResponse | null = yield call(setBID, bId);

            return response?.b_id;
        }

        const response: BIDResponse | null = yield call(getBID);

        return response?.b_id;
    }
    catch (error) {
        yield put(setError({ error: error as Error, type: ErrorType.ZP_API }));
        yield call(trackError, error as Error, ErrorType.ZP_API);
    }
    finally {
        yield put(stopLoading(action.type));
    }
}

/**
 * Handle login status persistence and gimli login in case of cable customer
 */
export function* handleLogin (action: Actions) {
    const customerType = getCustomerType();

    if (!customerType) {
        return;
    }

    try {
        yield put(startLoading(action.type));

        if (!isCableCustomer(customerType)) {
            yield put(setLoginStatus(customerType));

            return;
        }

        const response: LoginResponse | null = yield call(getGimliLogin);

        if (!response) {
            throw Error('Empty login response');
        }

        if (!response.success) {
            if (response.loginState === 'redirecting') {
                window.open(getUrl(Domains.ZUHAUSEPLUS + Endpoints.LOGIN_REDIRECT, {
                    [QueryNames.TARGET]: location.href,
                }), '_self');

                return;
            }

            throw Error('Login failed');
        }

        yield put(setLoginStatus(customerType));
    }
    catch (error) {
        yield put(setError({
            error: error as Error,
            type: ErrorType.AVC_ERROR,
        }));
        yield call(trackError, error as Error, ErrorType.AVC_ERROR);
    }
    finally {
        yield put(stopLoading(action.type));
    }
}

/**
 * Handles case when customer comes with the intention to buy 3P (Internet, Phone, TV)
 */
export function* handleStepper () {
    const is3PFlowByUrl: boolean = yield call(isCustomerIn3pFlowByUrl);
    const cookiePresent: boolean = yield call(isCookiePresent, Cookies.THREE_P);

    const { stepper3P, stepper2P }: StepperData = getCMSConfig().stepper || STEPPER_DATA;

    if (cookiePresent) {
        yield call(mountLinearStepper, Object.values(stepper3P.labels), stepper3P.activeStep);
        yield call(setElementVisibility, AppId.LINEAR_STEPPER, true);

        return;
    }

    if (!is3PFlowByUrl) {
        yield call(mountLinearStepper, Object.values(stepper2P.labels), stepper2P.activeStep);
        yield call(setElementVisibility, AppId.LINEAR_STEPPER, true);

        return;
    }

    yield call(set3PCookie);
    yield call(mountLinearStepper, Object.values(stepper3P.labels), stepper3P.activeStep);
    yield call(setElementVisibility, AppId.LINEAR_STEPPER, true);
}
